import { ChevronRightIcon, TriangleDownIcon, TriangleUpIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  Skeleton,
  Stack,
  Table,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
} from "@chakra-ui/react";
import {
  ColumnDef,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";

import { sortingFns } from "../../../../shared/utils/tanstack-table";

import { dateFormatter } from "../../../../shared/utils/date-formatter";
import { useEntityLink } from "../../../../shared/hooks/useEntityLink";
import { MergedRow } from "../hooks/useTasksV2Data";
import { useTasksV2Data } from "../hooks/useTasksV2Data";
import { useTasksV2Filters, useTasksV2OpenTask } from "../hooks/useTasksV2Filters";
import { TasksV2StatusBadge } from "./TasksV2StatusBadge";
import TasksV2DocumentsSummary from "./TasksV2DocumentsSummary";
import TasksV2NotesCell from "./TasksV2NotesCell";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatFullName(
  p: { firstName: string; middleName?: string | null; lastName: string } | null | undefined
): string {
  if (!p) return "—";
  return [p.firstName, p.middleName, p.lastName].filter(Boolean).join(" ") || "—";
}

function Muted({ children }: { children: React.ReactNode }) {
  return (
    <Text as="span" color="gray.400">
      {children}
    </Text>
  );
}

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

const { accessor } = createColumnHelper<MergedRow>();

function EntityCell({
  entity,
  type,
}: {
  entity:
    | { id: number; firstName: string; middleName?: string | null; lastName: string }
    | null
    | undefined;
  type: "Patient" | "Caregiver";
}) {
  const entityLink = useEntityLink({ redirectModal: false });
  if (!entity) return <Muted>—</Muted>;
  const fullName = formatFullName(entity);
  return (
    <Text
      _hover={{ color: "blue.700", textDecoration: "underline" }}
      as="a"
      color="blue.600"
      cursor="pointer"
      fontWeight={type === "Patient" ? "semibold" : "normal"}
      onClick={(event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();
        entityLink.open({ event, entity: { id: entity.id as never, type } });
      }}
    >
      {fullName}
    </Text>
  );
}

const columns = [
  accessor((row) => row.patient, {
    id: "patient",
    header: "Patient",
    cell: (info) => <EntityCell entity={info.getValue()} type="Patient" />,
  }),
  accessor((row) => row.caregiver, {
    id: "caregiver",
    header: "Caregiver",
    cell: (info) => <EntityCell entity={info.getValue()} type="Caregiver" />,
  }),
  accessor("taskName", {
    header: "Task",
    cell: (info) => {
      const row = info.row.original;
      const duplicateOfDate = row.__kind === "regular" ? row.duplicateOfDate : null;
      return (
        <Stack align="flex-start" spacing={1}>
          <Text fontWeight="medium">{info.getValue()}</Text>
          {duplicateOfDate != null && (
            <Tooltip
              hasArrow
              label={`Duplicated with a task from ${dateFormatter.toDate(duplicateOfDate)}`}
              placement="top"
            >
              <Tag borderRadius="full" colorScheme="orange" size="sm" variant="subtle">
                Duplicate
              </Tag>
            </Tooltip>
          )}
        </Stack>
      );
    },
  }),
  accessor((row) => row.contract?.contractType?.name ?? null, {
    id: "contract",
    header: "Contract",
    cell: (info) => info.getValue() ?? <Muted>—</Muted>,
  }),
  accessor("scheduledDate", {
    id: "scheduledDate",
    header: "Scheduled",
    cell: (info) => {
      const value = info.getValue();
      return value ? dateFormatter.toDate(value) : <Muted>—</Muted>;
    },
  }),
  accessor((row) => ({ kind: row.__kind, status: row.__kind === "regular" ? row.status : null }), {
    id: "status",
    header: "Status",
    cell: (info) => {
      const { status } = info.getValue();
      // Future rows have no status; render as INCOMPLETED per spec
      const normalizedStatus =
        status === "COMPLETED" || status === "INCOMPLETED" || status === "APPROVED" ? status : null;
      return <TasksV2StatusBadge status={normalizedStatus} />;
    },
  }),
  accessor(
    (row) => (row.__kind === "regular" ? { in: row.clockInTime, out: row.clockOutTime } : null),
    {
      id: "clock",
      header: "Clock in / out",
      cell: (info) => {
        const value = info.getValue();
        if (!value || (!value.in && !value.out)) {
          return <Muted>—</Muted>;
        }
        return (
          <>
            {value.in ? dateFormatter.toTime(value.in) : <Muted>—</Muted>}
            <Muted> – </Muted>
            {value.out ? dateFormatter.toTime(value.out) : <Muted>—</Muted>}
          </>
        );
      },
    }
  ),
  {
    id: "documents",
    header: () => (
      <Tooltip hasArrow label="Approved / completed / missing" placement="top">
        <span>Documents</span>
      </Tooltip>
    ),
    cell: (info) => {
      const row = info.row.original;
      if (row.__kind !== "regular") {
        return <Muted>—</Muted>;
      }
      return <TasksV2DocumentsSummary documents={row.documents.nodes} />;
    },
  } as ColumnDef<MergedRow>,
  accessor("createdAt", {
    header: "Created",
    cell: (info) => {
      const row = info.row.original;
      const value = info.getValue();
      const createdBy = row.__kind === "regular" ? row.createdByMember : null;
      if (!value) return <Muted>—</Muted>;
      return (
        <Tooltip hasArrow label={dateFormatter.toDateTime(value)} placement="top">
          <Box>
            <Text>{dateFormatter.toDate(value)}</Text>
            {createdBy && (
              <Text color="gray.500" fontSize="xs">
                by {formatFullName(createdBy)}
              </Text>
            )}
          </Box>
        </Tooltip>
      );
    },
  }),
  {
    id: "notes",
    header: "Notes",
    cell: (info) => {
      const row = info.row.original;
      if (row.__kind !== "regular") {
        return <Muted>—</Muted>;
      }
      // Editing the note must not also open the task.
      return (
        <Box maxW="220px" whiteSpace="normal" onClick={(e) => e.stopPropagation()}>
          <TasksV2NotesCell notes={row.notes ?? null} taskInstanceId={row.id} />
        </Box>
      );
    },
  } as ColumnDef<MergedRow>,
  {
    id: "open",
    header: () => null,
    cell: (info) =>
      info.row.original.__kind === "regular" ? (
        <ChevronRightIcon
          boxSize={5}
          className="tasks-v2-row-chevron"
          color="gray.300"
          transition="color 0.15s, transform 0.15s"
        />
      ) : null,
  } as ColumnDef<MergedRow>,
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function TasksV2Table() {
  const { rows, isPending, isError, refetch } = useTasksV2Data();
  const filters = useTasksV2Filters();
  const { taskInstanceId: openTaskInstanceId, openTask } = useTasksV2OpenTask();

  const sortDirection = filters.getValue("sortDirection") ?? "DESC";

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    sortingFns,
    manualPagination: true,
    manualSorting: true,
  });

  if (isPending) {
    return (
      <Stack p={4} spacing={3}>
        {Array.from({ length: 6 }, (_, i) => (
          <Skeleton key={i} borderRadius="md" h="48px" />
        ))}
      </Stack>
    );
  }

  if (isError) {
    return (
      <Flex align="center" direction="column" gap={3} py={16}>
        <Text color="red.500" fontWeight="medium">
          Failed to load tasks.
        </Text>
        <Button size="sm" onClick={refetch}>
          Retry
        </Button>
      </Flex>
    );
  }

  if (rows.length === 0) {
    return (
      <Flex align="center" direction="column" gap={2} py={16}>
        <Text fontSize="md" fontWeight="semibold">
          No tasks found
        </Text>
        <Text color="gray.500" fontSize="sm">
          Try widening the date range or removing some filters.
        </Text>
        <Button mt={2} size="sm" variant="outline" onClick={() => filters.resetToDefault()}>
          Clear filters
        </Button>
      </Flex>
    );
  }

  return (
    <Box overflowX="auto">
      <Table size="md" variant="simple">
        <Thead bg="gray.50">
          {table.getHeaderGroups().map((headerGroup) => (
            <Tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const isSortable = header.id === "scheduledDate";
                return (
                  <Th
                    key={header.id}
                    _hover={isSortable ? { color: "gray.700" } : undefined}
                    borderColor="gray.100"
                    color="gray.500"
                    cursor={isSortable ? "pointer" : undefined}
                    fontSize="11px"
                    fontWeight="semibold"
                    letterSpacing="wider"
                    px={4}
                    py={3}
                    userSelect={isSortable ? "none" : undefined}
                    whiteSpace="nowrap"
                    onClick={
                      isSortable
                        ? () => {
                            filters.setValues({
                              sortDirection: sortDirection === "ASC" ? "DESC" : "ASC",
                              page: 1,
                            });
                          }
                        : undefined
                    }
                  >
                    <Flex align="center" gap={1.5}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {isSortable &&
                        (sortDirection === "ASC" ? (
                          <TriangleUpIcon boxSize={2} />
                        ) : (
                          <TriangleDownIcon boxSize={2} />
                        ))}
                    </Flex>
                  </Th>
                );
              })}
            </Tr>
          ))}
        </Thead>
        <Tbody>
          {table.getRowModel().rows.map((row) => {
            const task = row.original;
            // Future occurrences have no instance yet, so nothing to open.
            const taskInstanceId = task.__kind === "regular" ? task.id : null;
            const isOpen = taskInstanceId !== null && taskInstanceId === openTaskInstanceId;

            return (
              <Tr
                key={row.id}
                _hover={
                  taskInstanceId !== null
                    ? {
                        bg: isOpen ? "blue.50" : "gray.50",
                        "& .tasks-v2-row-chevron": {
                          color: "gray.500",
                          transform: "translateX(2px)",
                        },
                      }
                    : undefined
                }
                bg={isOpen ? "blue.50" : undefined}
                cursor={taskInstanceId !== null ? "pointer" : undefined}
                transition="background 0.15s"
                onClick={taskInstanceId !== null ? () => openTask(taskInstanceId) : undefined}
              >
                {row.getVisibleCells().map((cell) => (
                  <Td
                    key={cell.id}
                    borderColor="gray.100"
                    color="gray.700"
                    fontSize="sm"
                    px={4}
                    py={3}
                    whiteSpace="nowrap"
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                ))}
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </Box>
  );
}
